import { Delete as DeleteIcon, Edit as EditIcon, PlayArrow as PlayIcon } from "@mui/icons-material";
import { Alert, Box, Card, IconButton, Typography } from "@mui/material";
import { getStatus } from "src/utils/getStatus";
import type { VideoData } from "./types";

interface VideoCardProps {
	video: VideoData;
	onEdit: (video: VideoData) => void;
	onDelete: (videoId: string) => void;
	isDeleting?: boolean;
}

export default function VideoCard({ video, onEdit, onDelete, isDeleting = false }: VideoCardProps) {
	const handleDelete = () => {
		if (window.confirm(`Are you sure you want to delete the video "${video.title}"?`)) {
			onDelete(video.id);
		}
	};

	return (
		<Card variant="outlined" sx={{ height: "100%", position: "relative" }}>
			<Box sx={{ position: "relative" }}>
				<Box
					sx={{
						width: "100%",
						height: 160,
						backgroundColor: "#f5f5f5",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						overflow: "hidden",
					}}
				>
					{video.image_uri ? (
						<img
							src={video.image_uri}
							alt={video.title}
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
							}}
							onError={(e) => {
								const target = e.target as HTMLImageElement;
								target.style.display = "none";
								target.parentElement!.innerHTML =
									'<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999;"><svg width="48" height="48" viewBox="0 0 24 24"><path fill="currentColor" d="M8,5.14V19.14L19,12.14L8,5.14Z" /></svg></div>';
							}}
						/>
					) : (
						<PlayIcon sx={{ fontSize: 48, color: "#ccc" }} />
					)}
				</Box>
				<Box
					sx={{
						position: "absolute",
						top: 8,
						right: 8,
						display: "flex",
						gap: 1,
					}}
				>
					<IconButton
						size="small"
						onClick={() => onEdit(video)}
						sx={{
							backgroundColor: "rgba(255, 255, 255, 0.9)",
							"&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
						}}
					>
						<EditIcon fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						onClick={handleDelete}
						sx={{
							backgroundColor: "rgba(255, 255, 255, 0.9)",
							"&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
						}}
						color="error"
						disabled={isDeleting}
					>
						<DeleteIcon fontSize="small" />
					</IconButton>
				</Box>
			</Box>
			<Box sx={{ p: 2, pb: 0 }}>
				<Typography variant="subtitle2" noWrap title={video.title} sx={{ fontWeight: 600 }}>
					{video.title || "Untitled"}
				</Typography>
				<Typography variant="caption" color="textSecondary" noWrap title={video.category} sx={{ display: "block", mb: 1 }}>
					{video.category || "No category"}
				</Typography>
				<Typography
					variant="caption"
					color="primary"
					noWrap
					title={video.video_uri}
					sx={{
						display: "block",
						textDecoration: video.video_uri ? "underline" : "none",
						cursor: video.video_uri ? "pointer" : "default",
					}}
					onClick={() => {
						if (video.video_uri) {
							window.open(video.video_uri, "_blank");
						}
					}}
				>
					{video.video_uri || "No URL"}
				</Typography>
			</Box>
			<Alert severity={video.status === 1 ? "success" : "error"} sx={{ mt: 2 }}>
				{getStatus(video.status)}
			</Alert>
		</Card>
	);
}
